// View helpers and the poll controller of the AI Optimise tab on the strategies page. Plain CommonJS (it may require
// lodash and the pure label map only, no React), so it can be tested in plain node -- see src/lib/action-outcome.js and test/ai-optimization.js.
const { omit, isEqual, isNil, isPlainObject } = require('lodash');
const { changeLabel, formatValue, getAtPointer } = require('./strategy-change-labels');

// Nothing here computes a profit of its own: every number shown comes from the stored run row, and a progress field the
// daemon has not written yet (null) is shown as "not available", never as 0.

const POLL_INTERVAL_MS = 5000;
const PAGE_SIZE = 25;
const MAX_LOADED_RUNS = 100;
const ACTIVE_STATUSES = ['queued', 'running'];

const STATUS_LABELS = {
	queued: 'Queued',
	running: 'Running',
	paused: 'Paused',
	done: 'Done',
	not_found: 'Not found',
	failed: 'Failed',
	cancelled: 'Cancelled',
};

// Tailwind JIT needs every class as a whole literal string: a lookup object, never a template-built class name
const STATUS_PILL_CLASSES = {
	queued: 'bg-sky-700',
	running: 'bg-indigo-600',
	paused: 'bg-orange-600',
	done: 'bg-green-600',
	not_found: 'bg-amber-600',
	failed: 'bg-red-600',
	cancelled: 'bg-gray-600',
};

const PILL_BASE_CLASS = 'py-1 px-2 rounded-full text-center text-white whitespace-nowrap text-xs';

// the keys the strategies page rewrites by itself (results of a simulation): they are not an edit of the strategy
const RESULT_KEYS = ['strategyresult', 'tradesresult', 'tradesresultscore', 'maturity'];

const FINISH_REASON_LABELS = {
	time_budget: 'time budget used',
	max_rounds: 'all rounds used',
	search_exhausted: 'the AI had nothing new to try',
	cancelled: 'cancelled',
	ai_error: 'the AI advisor failed',
	engine_error: 'engine error',
	calibration_failed: 'too slow to search within the budget',
	window_error: 'the candle window could not be loaded',
	owner_lost: 'the simulator stopped reporting',
	interrupted: 'interrupted twice, not re-queued',
	strategy_missing: 'the strategy no longer exists',
	internal: 'internal error',
	openai_funds: 'OpenAI has no funds',
};

const BADGE_CLASSES = { applied: 'bg-green-800', outdated: 'bg-gray-700' };

const FAILED_TEXT_MAX = 80;

const CANCEL_RUNNING_CONFIRM = 'Cancel this running AI run? The search so far is discarded and nothing is applied.';

const CANCEL_PAUSED_CONFIRM = 'Cancel this paused AI run? The rounds done so far are discarded and nothing is applied.';

// The popover texts of the red info icon (D-38, D-39): a paused run gives its stored message followed by the hint, a queued
// run that waits behind the hold says why
const PAUSED_HINT = 'Top up OpenAI, then Continue.';
const HELD_TEXT = 'Waiting: OpenAI has no funds.';

const UNSAVED_EDITS_TOAST = 'The run uses the saved strategy: your unsaved edits are not part of it.';

const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const isNumber = value => typeof value === 'number' && Number.isFinite(value);

// The stop-loss (in %) an AI run searches under, chosen next to Start. The list exists in three repos and must stay equal:
// trading-api src/lib/ai-optimizations.js AI_RUN_STOPLOSS_CHOICES (the authority) and the daemon's src/optimizer/registry.rs
// RUN_STOPLOSS_CHOICES; test/ai-optimization.js checks all three. trading-api refuses any other value with HTTP 400
const RUN_STOPLOSS_CHOICES = Object.freeze([5, 8, 10, 12, 15, 20]);
// The built-in value Start preselects when neither this session nor trading-api's settings row names one (D-03)
const DEFAULT_RUN_STOPLOSS = 5;

// A select hands over text ('15'), trading-api wants the number 15: only a choice of the list comes out, else null
const parseRunStoploss = value => {
	let number = null;
	if (isNumber(value)) {
		number = value;
	} else if (typeof value === 'string' && value.trim() !== '') {
		number = Number(value);
	}
	return isNumber(number) && RUN_STOPLOSS_CHOICES.includes(number) ? number : null;
};

// The rounds an AI run may use, chosen on the Rounds toggle next to Start. The list exists in three repos and must stay equal:
// trading-api src/lib/ai-optimizations.js AI_RUN_ROUNDS_CHOICES (the authority) and the daemon's src/optimizer/registry.rs
// RUN_ROUNDS_CHOICES; test/ai-optimization.js checks all three. trading-api refuses any other value with HTTP 400
const RUN_ROUNDS_CHOICES = Object.freeze([25, 50]);
const DEFAULT_RUN_ROUNDS = 25;

// Rounds go out as the JSON number 25 or 50 (never the text '50'): only a choice of the list comes out, else null
const parseRunRounds = value => {
	let number = null;
	if (isNumber(value)) {
		number = value;
	} else if (typeof value === 'string' && value.trim() !== '') {
		number = Number(value);
	}
	return isNumber(number) && RUN_ROUNDS_CHOICES.includes(number) ? number : null;
};

// The double toggle counts its sides 0 (none), 1 (left) and 2 (right); the left side is the first choice, the right the second
const roundsToggleState = value => {
	const index = RUN_ROUNDS_CHOICES.indexOf(value);
	return index === -1 ? 0 : index + 1;
};

// The side the toggle reports (1 or 2) as the rounds of that side; anything else is no choice at all
const roundsOfToggleState = state => {
	if (!Number.isInteger(state) || state < 1 || state > RUN_ROUNDS_CHOICES.length) {
		return null;
	}
	return RUN_ROUNDS_CHOICES[state - 1];
};

// The three per-run judging ranges of the start box (D-27). Each exists in more than one repo and must stay equal: trading-api
// src/lib/ai-optimizations.js AI_RUN_MIN_GOOD_TRADES_RANGE / AI_RUN_STOPLOSS_COVER_RANGE / AI_RUN_NEIGHBOUR_STEPS_RANGE (the
// authority) and the daemon's MIN_GOOD_TRADES_FLOOR / MIN_GOOD_TRADES_CEILING and MAX_STOPLOSS_COVER_HUNDREDTHS (src/optimizer/judge.rs),
// MAX_NEIGHBOUR_STEPS (src/optimizer/neighbour.rs); test/ai-optimization.js checks all of them. trading-api refuses anything else with HTTP 400
const MIN_GOOD_TRADES_RANGE = Object.freeze([1, 1000]);
const STOPLOSS_COVER_RANGE = Object.freeze([0, 100]);
const NEIGHBOUR_STEPS_RANGE = Object.freeze([0, 10]);

// A text field hands over text: a number, or a trimmed text of plain digits ('30'), converted to the number 30; only a value inside the range counts.
// Anything else (30.5, '3e1', '-1', '1,5', '', null, true) is no value at all
const parseWholeNumber = (value, [min, max]) => {
	let number = null;
	if (isNumber(value)) {
		number = value;
	} else if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
		number = Number(value.trim());
	}
	return Number.isInteger(number) && number >= min && number <= max ? number : null;
};

// The good trades a run must have at least: a whole number from 1 to 1000
const parseMinGoodTrades = value => parseWholeNumber(value, MIN_GOOD_TRADES_RANGE);

// The levels / other-settings steps of the neighbour check: a whole number from 0 to 10 (0 is a valid choice: that part is not checked)
const parseNeighbourSteps = value => parseWholeNumber(value, NEIGHBOUR_STEPS_RANGE);

// The cover of unforeseen stop-losses: a number from 0 to 100 with at most 2 decimals. Text must be plain decimal digits
// ('1.5', '1.50'; not '1,5', '.5', '1.555', '3e1'); a number must be whole hundredths. '1.50' goes out as the number 1.5
const parseStoplossCover = value => {
	let number = null;
	if (isNumber(value)) {
		number = Math.abs(value * 100 - Math.round(value * 100)) < 1e-9 ? value : null;
	} else if (typeof value === 'string' && /^\d+(\.\d{1,2})?$/.test(value.trim())) {
		number = Number(value.trim());
	}
	return isNumber(number) && number >= STOPLOSS_COVER_RANGE[0] && number <= STOPLOSS_COVER_RANGE[1] ? number : null;
};

// The six settings of the start box, each with its parser: the internal (camelCase) key and the parser of its values
const START_SETTING_PARSERS = Object.freeze({
	stoploss: parseRunStoploss,
	rounds: parseRunRounds,
	minGoodTrades: parseMinGoodTrades,
	stoplossCover: parseStoplossCover,
	neighbourLevelSteps: parseNeighbourSteps,
	neighbourOtherSteps: parseNeighbourSteps,
});

// The four per-run judging settings, as the internal keys and as the keys of the request of create-ai-optimization and of the
// answer of get-ai-run-defaults (trading-api AI_RUN_JUDGING_FIELDS keys, same order)
const JUDGING_START_KEYS = Object.freeze([
	'minGoodTrades',
	'stoplossCover',
	'neighbourLevelSteps',
	'neighbourOtherSteps',
]);
const JUDGING_WIRE_KEYS = Object.freeze([
	'mingoodtrades',
	'stoplosscover',
	'neighbourlevelsteps',
	'neighbourothersteps',
]);

// The reason line of each judging field that cannot be sent (D-27); the panel renders them as text under the controls
const START_PROBLEM_TEXTS = Object.freeze({
	minGoodTrades: 'Minimum Good Trades: a whole number from 1 to 1000',
	stoplossCover: 'Cover unforeseen stop-losses: a number from 0 to 100 with at most 2 decimals',
	neighbourLevelSteps: 'Neighbour steps: levels: a whole number from 0 to 10',
	neighbourOtherSteps: 'Neighbour steps: other settings: a whole number from 0 to 10',
});

// The text of a field as a trimmed string ('' for a missing or non-text value)
const startTextOf = (texts, key) => {
	const text = texts?.[key];
	return typeof text === 'string' ? text.trim() : '';
};

// Plain decimal text only ('3.4', '15', '3.40'): Number() alone would also read '', '1e1' and '0x10' as numbers
const NUMERIC_TEXT = /^\s*\d+(\.\d+)?\s*$/;

// The stop-loss of a strategy as a number of at least 0: a JS number, or the numeric text a Postgres numeric column hands
// out. Anything else (missing, null, NaN, negative, other text) is unknown: null, never an invented value
const strategyStoplossValue = value => {
	let number = null;
	if (isNumber(value)) {
		number = value;
	} else if (typeof value === 'string' && NUMERIC_TEXT.test(value)) {
		number = Number(value);
	}
	return isNumber(number) && number >= 0 ? number : null;
};

// D-46: the cover is a multiple of the strategy's own stop-loss, so a strategy without one (0 is off) cannot run with a cover
// above 0: the daemon refuses such a run, and Start does not queue it. The text follows the other problem texts: the label, a
// colon, the reason
const COVER_NEEDS_OWN_STOPLOSS_TEXT =
	'Cover unforeseen stop-losses: the strategy has no stop-loss of its own (enter 0 to run without the cover)';

// Whether the cover Start would send is above 0 while the strategy's own stop-loss is 0. The cover is the typed one when it
// is valid, and the published default when the field is empty (an empty field uses the daemon setting). With the strategy's
// stop-loss unknown, an invalid typed cover, or no known default, nothing is refused here: the daemon stays the authority
const coverNeedsOwnStoploss = ({ coverText: typed, defaultCover, strategyStoploss }) => {
	if (strategyStoplossValue(strategyStoploss) !== 0) {
		return false;
	}
	const cover = typed === '' ? defaultCover : parseStoplossCover(typed);
	return isNumber(cover) && cover > 0;
};

// The problems that must disable Start, one message per judging field in field order. Invalid text is always a problem; an
// empty field is a problem only when a default (published or remembered) is known for it: with none known it is valid and
// sends nothing, so the daemon uses its own setting (D-28). known: one boolean per judging key. strategyStoploss (the saved
// strategy's own, any shape) and defaultCover (the published or remembered cover, a number or null) add the D-46 problem right
// after the cover field's own
const startSettingsProblems = ({ texts, known, strategyStoploss, defaultCover } = {}) =>
	JUDGING_START_KEYS.flatMap(key => {
		const text = startTextOf(texts, key);
		const problems = [];
		if (text === '' ? known?.[key] === true : START_SETTING_PARSERS[key](text) === null) {
			problems.push(START_PROBLEM_TEXTS[key]);
		}
		if (key === 'stoplossCover' && coverNeedsOwnStoploss({ coverText: text, defaultCover, strategyStoploss })) {
			problems.push(COVER_NEEDS_OWN_STOPLOSS_TEXT);
		}
		return problems;
	});

// Shown under the controls when both neighbour steps are 0: that run does not check the neighbours at all
const NEIGHBOUR_OFF_START_TEXT = 'The neighbour check is off for this run.';

// The neighbour-off note: only when both step fields hold a valid 0 (one 0 and the other 1, empty or invalid says nothing)
const neighbourOffStartText = texts =>
	parseNeighbourSteps(startTextOf(texts, 'neighbourLevelSteps')) === 0 &&
	parseNeighbourSteps(startTextOf(texts, 'neighbourOtherSteps')) === 0
		? NEIGHBOUR_OFF_START_TEXT
		: null;

// Shown under the controls when a field is empty without a known default: the daemon's own setting is then used. An empty
// field with a known default is a problem instead (startSettingsProblems). ready is the defaults read having settled:
// while it is pending the fields are empty only because nothing arrived yet, and the line must not flash (Phase 10 no-flash rule)
const EMPTY_START_SETTING_TEXT = 'An empty field uses the daemon setting.';

const emptyStartSettingText = ({ ready, texts, known } = {}) => {
	if (ready !== true) {
		return null;
	}
	const anyEmptyUnknown = JUDGING_START_KEYS.some(key => startTextOf(texts, key) === '' && known?.[key] !== true);
	return anyEmptyUnknown ? EMPTY_START_SETTING_TEXT : null;
};

// What Start preselects, per value on its own: the one remembered from this session (the last valid change of that
// control), else the one the daemon published through trading-api, else the built-in one (5 % stop-loss, 25 rounds). The four
// judging settings have no built-in value (null: the field stays empty and Start sends nothing for it, D-28). Each is checked
// against its parser; an invalid one falls through to the next source
const resolveStartValues = ({ remembered, configured } = {}) => {
	const pick = (key, builtin) => {
		const parse = START_SETTING_PARSERS[key];
		const found = [remembered, configured].map(source => parse(source?.[key])).find(value => value !== null);
		return found === undefined ? builtin : found;
	};
	return {
		stoploss: pick('stoploss', DEFAULT_RUN_STOPLOSS),
		rounds: pick('rounds', DEFAULT_RUN_ROUNDS),
		minGoodTrades: pick('minGoodTrades', null),
		stoplossCover: pick('stoplossCover', null),
		neighbourLevelSteps: pick('neighbourLevelSteps', null),
		neighbourOtherSteps: pick('neighbourOtherSteps', null),
	};
};

// The controls' first state. With a remembered value or an answer of the settings read the values are known at once (no flash
// from the built-in values to the configured ones); with neither, the controls wait (ready false, no value, no pressed side)
const initialStartState = ({ remembered, configured } = {}) => {
	if (isNil(remembered) && isNil(configured)) {
		return {
			ready: false,
			stoploss: null,
			rounds: null,
			minGoodTrades: null,
			stoplossCover: null,
			neighbourLevelSteps: null,
			neighbourOtherSteps: null,
		};
	}
	return { ready: true, ...resolveStartValues({ remembered, configured }) };
};

// The start settings this session's user changed (D-29): each valid change of a control is remembered under its key, so
// switching strategies keeps them. Lifetime: the page (a reload or a new tab starts empty and shows the published defaults
// again); bound: one number per start setting (six). It lives in memory only and is never written to the browser's storage
const createStartMemory = () => {
	const values = {};
	return {
		recall: () => (Object.keys(values).length === 0 ? null : { ...values }),
		remember: (key, value) => {
			if (typeof key !== 'string' || !has(START_SETTING_PARSERS, key)) {
				return;
			}
			const next = START_SETTING_PARSERS[key](value);
			if (next === null) {
				return;
			}
			values[key] = next;
		},
	};
};

// The judging values of a start as the request keys of create-ai-optimization: only the ones that are known (neither null nor
// undefined, a missing key counts as unknown), as the numbers they are. Never null, never a text: trading-api refuses both, and
// a left-out key makes the daemon use its own setting (D-28, D-30)
const startRequestValues = (values = {}) => {
	const out = {};
	JUDGING_START_KEYS.forEach((key, index) => {
		const value = values?.[key];
		if (value !== null && value !== undefined) {
			out[JUDGING_WIRE_KEYS[index]] = value;
		}
	});
	return out;
};

// How long a panel waits for the first read of the configured defaults before it falls back to the built-in values: the api
// call has no timeout of its own, so a hung request would otherwise leave the controls disabled for the life of the page
const START_DEFAULTS_TIMEOUT_MS = 3000;

// Reads trading-api's configured Start defaults once per page. Lifetime: the page; bound: one object of at most six numbers
// (cached) and at most one read in flight, shared by every panel that opens meanwhile. Each load() races that read against
// a timer of its own and clears it when the read settles first, so no timer outlives its load(). A late answer after the
// timeout only fills the cache. A failed read (refusal, older trading-api, network, odd answer) is not cached, so the next
// panel retries. load() resolves to the cached object, or null on a failure or a timeout, and never rejects. The timer
// defaults look up the global setTimeout / clearTimeout at each call: a browser timer function called as a method of this
// options object would throw "Illegal invocation"
const createStartDefaultsLoader = ({
	fetchDefaults,
	timeoutMs = START_DEFAULTS_TIMEOUT_MS,
	setTimer = (fn, ms) => setTimeout(fn, ms),
	clearTimer = handle => clearTimeout(handle),
} = {}) => {
	let cached = null;
	let inFlight = null;
	const read = () => {
		if (inFlight === null) {
			inFlight = (async () => {
				try {
					const outcome = await fetchDefaults();
					if (outcome?.ok && isPlainObject(outcome.data)) {
						const answer = {};
						const stoploss = parseRunStoploss(outcome.data.stoploss);
						const rounds = parseRunRounds(outcome.data.rounds);
						if (stoploss !== null) {
							answer.stoploss = stoploss;
						}
						if (rounds !== null) {
							answer.rounds = rounds;
						}
						// the four judging values: a JSON number inside its range, else the key is left out (a text is not a value)
						JUDGING_WIRE_KEYS.forEach((wireKey, index) => {
							const key = JUDGING_START_KEYS[index];
							const raw = outcome.data[wireKey];
							const parsed = isNumber(raw) ? START_SETTING_PARSERS[key](raw) : null;
							if (parsed !== null) {
								answer[key] = parsed;
							}
						});
						cached = answer;
						return answer;
					}
				} catch (error) {
					// a failed read is the built-in values, not an error for the user
				}
				return null;
			})().finally(() => {
				inFlight = null;
			});
		}
		return inFlight;
	};
	return {
		cached: () => cached,
		load: () => {
			if (cached !== null) {
				return Promise.resolve(cached);
			}
			return new Promise(resolve => {
				let settled = false;
				const timer = setTimer(() => {
					if (!settled) {
						settled = true;
						resolve(null);
					}
				}, timeoutMs);
				read().then(value => {
					if (!settled) {
						settled = true;
						clearTimer(timer);
						resolve(value);
					}
				});
			});
		},
	};
};

// 15 -> '15%', 3.3 -> '3.3%' (no space before %, no float noise); null for anything that is not a number
const formatPercent = value => {
	if (!isNumber(value)) {
		return null;
	}
	return `${Math.round(value * 10000) / 10000}%`;
};

// The toast after a successful Start: the stop-loss and the rounds the server stored (echoed), else the ones that were sent
const startToastText = ({ echoed, sent, echoedRounds, sentRounds } = {}) => {
	let value = null;
	if (isNumber(echoed)) {
		value = echoed;
	} else if (isNumber(sent)) {
		value = sent;
	}
	let rounds = null;
	if (isNumber(echoedRounds)) {
		rounds = echoedRounds;
	} else if (isNumber(sentRounds)) {
		rounds = sentRounds;
	}
	const text =
		value === null ? 'AI optimise run queued' : `AI optimise run queued at ${formatPercent(value)} stop-loss`;
	return rounds === null ? text : `${text}, ${rounds} rounds`;
};

// A run before trading-api 0.8.0 searched the fixed 8 / 8.5 / 9 % grid and has no stop-loss of its own
const V10_GRID_LABEL = '8–9% (v1.0 grid)';

// The run's stop-loss is a number exactly when it is a v1.1 run: null, a missing key and any non-number mean v1.0
// (trading-api sends runstoploss: null for v1.0 rows, and applies the same rule)
const runStoploss = run => (isNumber(run?.runstoploss) ? run.runstoploss : null);

const runStoplossLabel = run => {
	const value = runStoploss(run);
	return value === null ? V10_GRID_LABEL : formatPercent(value);
};

// The run list's Stop-loss column is short: a v1.0 run reads a plain '8–9%'. The detail header keeps the longer label
// above, which says why that range is shown
const V10_LIST_LABEL = '8–9%';

const runStoplossListLabel = run => {
	const value = runStoploss(run);
	return value === null ? V10_LIST_LABEL : formatPercent(value);
};

// The stop-loss of a strategy: 0 is off in the engine; anything that is unknown (see strategyStoplossValue) is left out
const strategyStoplossText = value => {
	const number = strategyStoplossValue(value);
	if (number === null) {
		return null;
	}
	return number === 0 ? 'off' : formatPercent(number);
};

// Next to the Start dropdown: the stop-loss the saved strategy has itself (never the form's edited copy), in
// parentheses (D-14)
const startHint = savedStrategy => {
	const text = strategyStoplossText(savedStrategy?.stoplosspercent);
	return text === null ? null : `(Strategy's own: ${text})`;
};

// The detail header: the run's stop-loss and, for a v1.1 run that stored it, the strategy's own when the run started.
// Read from the run's metadata only, never from the strategy as it is now
const runStoplossHeaderText = run => {
	const head = `stop-loss ${runStoplossLabel(run)}`;
	if (runStoploss(run) === null) {
		return head;
	}
	const had = strategyStoplossText(run?.metadata?.strategyStoploss);
	return had === null ? head : `${head} (strategy had ${had} when the run started)`;
};

// The rounds a run has: the stored runrounds (25 or 50, trading-api 0.10.0 on), else the limit the daemon reported in
// maxrounds (an older run: 20), else the budget in the detail row's metadata. Only a JSON number counts (a text falls
// through to the next source), and when none is known the answer is null: never an invented 20 or 25. The list rows carry
// no metadata, so there the chain ends at maxrounds
const roundsOf = run => {
	const sources = [run?.runrounds, run?.maxrounds, run?.metadata?.budget?.maxRounds];
	const found = sources.find(isNumber);
	return found === undefined ? null : found;
};

// The run list's Rounds column: the number as text, or a dash when the run has none
const runRoundsListLabel = run => {
	const rounds = roundsOf(run);
	return rounds === null ? '—' : String(rounds);
};

// The detail header: '50 rounds' ('1 round'), or null when the run has no known rounds (nothing is rendered then)
const runRoundsHeaderText = run => {
	const rounds = roundsOf(run);
	if (rounds === null) {
		return null;
	}
	return rounds === 1 ? '1 round' : `${rounds} rounds`;
};

// A running run whose cancel the daemon has not confirmed yet reads 'Cancelling…'; an unknown status shows as it is
const statusView = run => {
	const status = run?.status;
	if (status === 'running' && run.cancelrequested) {
		return { label: 'Cancelling…', pillClass: STATUS_PILL_CLASSES.running };
	}
	if (has(STATUS_LABELS, status)) {
		return { label: STATUS_LABELS[status], pillClass: STATUS_PILL_CLASSES[status] };
	}
	return { label: String(status ?? ''), pillClass: 'bg-gray-600' };
};

const isActiveRun = run => ACTIVE_STATUSES.includes(run?.status);

const hasActiveRun = runs => Array.isArray(runs) && runs.some(isActiveRun);

// The text of the red info icon of a row, or null when the row has none. A paused run reads its message (trimmed) plus the
// hint, or the hint alone; a queued run reads the waiting text only when the server says held is strictly true. A run is
// never both: the status decides, so a stray held flag on a paused or running run changes nothing
const runAlert = run => {
	if (run?.status === 'paused') {
		const message = String(run.error ?? '').trim();
		return { text: message ? `${message} ${PAUSED_HINT}` : PAUSED_HINT };
	}
	if (run?.status === 'queued' && run.held === true) {
		return { text: HELD_TEXT };
	}
	return null;
};

// A paused run is not active (no 5-second poll, nothing changes until the user continues), so the Continue button has its own test
const hasPausedRun = runs => Array.isArray(runs) && runs.some(run => run?.status === 'paused');

// The confirmation after Continue: how many paused runs went back to the queue
const continueText = requeued => {
	if (requeued === 1) {
		return '1 AI run continued';
	}
	return requeued > 0 ? `${requeued} AI runs continued` : 'No AI runs were waiting';
};

// The one owner of a Continue in flight (the panel's button, T-09-24): run() sends the request, tells the activity channel
// 'continue' only after the server said yes, and answers null without a second request while one is still pending.
// continueRequest() -> promise of { ok, data: { requeued } } or { ok: false, error, sessionEnded }; notify(kind) is
// lib/ai-activity's notifyAiActivity. A request that throws is a refusal, never an unhandled rejection
const createContinueFlow = ({ continueRequest, notify }) => {
	let pending = false;
	return {
		isPending() {
			return pending;
		},
		async run() {
			if (pending) {
				return null;
			}
			pending = true;
			try {
				let outcome;
				try {
					outcome = await continueRequest();
				} catch (error) {
					outcome = { ok: false, error: error?.message || 'the request failed' };
				}
				if (outcome?.ok) {
					const requeued = Number(outcome.data?.requeued);
					const count = Number.isFinite(requeued) && requeued > 0 ? requeued : 0;
					notify('continue');
					return { ok: true, requeued: count, text: continueText(count) };
				}
				return {
					ok: false,
					text: `AI runs could not be continued: ${outcome?.error ?? 'the server did not confirm it'}`,
					sessionEnded: outcome?.sessionEnded === true,
				};
			} finally {
				pending = false;
			}
		},
	};
};

// An activity-channel listener that reacts to a Continue only: 'start' and 'cancel' already reload the panel themselves
const continueListener = onContinue => kind => {
	if (kind === 'continue') {
		onContinue();
	}
};

// Profits are compared in integer cents: the stored profits have 2 decimals and float noise (0.1 + 0.2) must not matter
const profitCents = value => {
	if (value === null || value === undefined) {
		return null;
	}
	const number = parseFloat(value);
	if (!Number.isFinite(number)) {
		return null;
	}
	return Math.round(number * 100);
};

const formatProfit = value => {
	const cents = profitCents(value);
	if (cents === null) {
		return null;
	}
	return (cents / 100).toFixed(2);
};

// 375 -> '06:15', 6000 -> '100:00' (minutes keep counting past 60, a run can overrun its budget)
const formatClock = sec => {
	if (!isNumber(sec)) {
		return null;
	}
	const total = Math.max(0, Math.floor(sec));
	const minutes = String(Math.floor(total / 60)).padStart(2, '0');
	const seconds = String(total % 60).padStart(2, '0');
	return `${minutes}:${seconds}`;
};

// 3600 -> '60 mins', 90 -> '2 mins' (rounded up, so the limit is never understated), 60 -> '1 min'
const formatMaxMinutes = sec => {
	if (!isNumber(sec)) {
		return null;
	}
	const minutes = Math.ceil(Math.max(0, sec) / 60);
	return minutes === 1 ? '1 min' : `${minutes} mins`;
};

const progressView = run => {
	const { currentround, maxrounds, elapsedsec, budgetsec, evaluations, requeues } = run || {};
	const best = formatProfit(run?.bestdailyprofit);
	const baseline = formatProfit(run?.baselinedailyprofit);

	// The bar fills by round, like the "round N of M" text above it; before round 1 it is empty
	let fraction = null;
	if (isNumber(maxrounds) && maxrounds > 0) {
		const round = isNumber(currentround) ? currentround : 0;
		fraction = Math.min(1, Math.max(0, round / maxrounds));
	}

	let requeuedText = null;
	if (run?.status === 'queued' && isNumber(requeues) && requeues >= 1) {
		requeuedText = 're-queued after an interruption';
	}

	return {
		roundText:
			isNumber(currentround) && isNumber(maxrounds) ? `round ${currentround} of ${maxrounds}` : 'not started yet',
		elapsedText:
			isNumber(elapsedsec) && isNumber(budgetsec)
				? `${formatClock(elapsedsec)} of max ${formatMaxMinutes(budgetsec)}`
				: null,
		fraction,
		bestText: best === null ? 'no qualifier yet' : `${best}/day`,
		baselineText: baseline === null ? 'baseline not measured yet' : `${baseline}/day`,
		evaluationsText: isNumber(evaluations) ? `${evaluations.toLocaleString('en-US')} candidates evaluated` : null,
		requeuedText,
	};
};

// Newest created first, then id descending when two created values are equal: the server's own order
const compareRuns = (a, b) => {
	const byCreated = (Date.parse(b?.created) || 0) - (Date.parse(a?.created) || 0);
	if (byCreated !== 0) {
		return byCreated;
	}
	const idA = String(a?.id ?? '');
	const idB = String(b?.id ?? '');
	if (idA === idB) {
		return 0;
	}
	return idA < idB ? 1 : -1;
};

// The refreshed first page replaces the loaded runs with the same id; older loaded runs that are not in it stay
const mergeFirstPage = (loaded, page) => {
	const byId = new Map();
	(Array.isArray(loaded) ? loaded : []).forEach(run => {
		byId.set(run.id, run);
	});
	(Array.isArray(page) ? page : []).forEach(run => {
		byId.set(run.id, run);
	});
	return Array.from(byId.values()).sort(compareRuns).slice(0, MAX_LOADED_RUNS);
};

// Does the form hold edits of the saved strategy that the run (which uses the saved one) does not include?
const unsavedEdits = (form, saved) => {
	if (!form || !saved || !form.id || form.id !== saved.id) {
		return false;
	}
	return !isEqual(omit(form, RESULT_KEYS), omit(saved, RESULT_KEYS));
};

// 'Load more' page: older runs go after the loaded ones. A run that is already loaded (a new run shifted the offsets)
// stays once, with the loaded -- fresher -- values
const appendRunPage = (loaded, page) => {
	const byId = new Map();
	(Array.isArray(page) ? page : []).forEach(run => {
		byId.set(run.id, run);
	});
	(Array.isArray(loaded) ? loaded : []).forEach(run => {
		byId.set(run.id, run);
	});
	return Array.from(byId.values()).sort(compareRuns).slice(0, MAX_LOADED_RUNS);
};

// 'Load more' is offered after a full page only, and while fewer than MAX_LOADED_RUNS runs are held
const hasMorePages = (lastPageLength, loadedCount) => lastPageLength === PAGE_SIZE && loadedCount < MAX_LOADED_RUNS;

// Never blank: a reason this tab has no label for is shown as the code the server sent
const finishReasonText = code => {
	if (has(FINISH_REASON_LABELS, code)) {
		return FINISH_REASON_LABELS[code];
	}
	return code ? String(code) : 'reason not stored';
};

// Why a finished run ended. A done or not_found run that stopped before its last round (it did not end on max_rounds, and
// its currentround is a number below the rounds it had) says where it stopped: 'ended at round 31 of 50 · time budget
// used'. That phrase is for done and not_found only: their currentround is the number of rounds played, while a failed,
// cancelled or paused run's is the round in progress. The run list's Progress cell (outcomeText) and the run detail's
// 'Finished:' lines both take their text from here (D-14, D-29)
const runFinishText = run => {
	const status = run?.status;
	if ((status === 'done' || status === 'not_found') && run.finishreason !== 'max_rounds') {
		const total = roundsOf(run);
		if (isNumber(run.currentround) && total !== null && run.currentround < total) {
			return `ended at round ${run.currentround} of ${total} · ${finishReasonText(run.finishreason)}`;
		}
	}
	return finishReasonText(run?.finishreason);
};

// How a finished run ended, for the Progress column; null while the run is still queued or running. A done or not_found run
// reads its status and runFinishText (the round it reached, when it stopped early, then the reason). A paused run reads its
// stored message cut like a failed run's (the whole text sits in the red icon's popover), or 'Paused' when it has none
const outcomeText = run => {
	const status = run?.status;
	if (status === 'done' || status === 'not_found') {
		return `${STATUS_LABELS[status]} · ${runFinishText(run)}`;
	}
	if (status === 'failed' || status === 'paused') {
		const error = String(run.error ?? '').trim();
		if (!error) {
			return status === 'paused' ? STATUS_LABELS.paused : finishReasonText(run.finishreason);
		}
		return error.length > FAILED_TEXT_MAX ? `${error.slice(0, FAILED_TEXT_MAX - 1)}…` : error;
	}
	if (status === 'cancelled') {
		return 'Cancelled';
	}
	return null;
};

// The strategy changed after the run started: a warning, it does not stop Apply (trading-api 0.9.1 sends outdated; an
// older one sent the strategy_changed reason instead and refused the Apply)
const isOutdated = run => run?.outdated === true || run?.appliableReason === 'strategy_changed';

// The history pills of a finished row: applied, or outdated because the strategy changed since the run
const rowBadges = run => {
	if (run?.appliableReason === 'already_applied' || run?.appliedat) {
		return ['applied'];
	}
	if (isOutdated(run)) {
		return ['outdated'];
	}
	return [];
};

// Changes when the saved strategy's active flag or engine settings change, so the run list is re-read (appliable
// follows them); results of a simulation are left out
const appliabilitySignature = saved => {
	if (!saved) {
		return '';
	}
	return JSON.stringify({ active: !!saved.active, config: omit(saved, RESULT_KEYS) });
};

// Cancelling a running or a paused run discards paid rounds, so it asks first; a queued run is cancelled at once
const cancelNeedsConfirm = run => run?.status === 'running' || run?.status === 'paused';

// The question of that confirm: the paused text says the rounds done so far are discarded, the running text the search
const cancelConfirmText = run => (run?.status === 'paused' ? CANCEL_PAUSED_CONFIRM : CANCEL_RUNNING_CONFIRM);

// The one owner of the poll timer of a mounted panel (5 s) or of the header's spend poll. sync(true) makes sure exactly
// one timer runs, sync(false) cancels it, stop() cancels it for good: after stop() every sync is a no-op and every
// sequence number is stale, so a fetch that resolves late can neither apply its answer nor start a new timer. begin()
// hands out a sequence number per fetch; isCurrent(seq) is true only for the newest one. The spend poll changes the
// interval through setIntervalMs, which cancels the running timer before it arms the new one, so there is still exactly
// one timer.
const createRunPoller = ({ tick, schedule, intervalMs }) => {
	let timer = null;
	let stopped = false;
	let sequence = 0;
	let currentMs = intervalMs;

	const cancelTimer = () => {
		if (timer) {
			timer.cancel();
			timer = null;
		}
	};

	const arm = () => {
		timer = schedule(tick, currentMs, currentMs);
	};

	return {
		sync(active) {
			if (stopped) {
				return;
			}
			if (active) {
				if (!timer) {
					arm();
				}
			} else {
				cancelTimer();
			}
		},
		setIntervalMs(ms) {
			if (stopped || ms === currentMs) {
				return;
			}
			currentMs = ms;
			if (timer) {
				cancelTimer();
				arm();
			}
		},
		intervalMs() {
			return currentMs;
		},
		stop() {
			stopped = true;
			sequence += 1;
			cancelTimer();
		},
		begin() {
			sequence += 1;
			return sequence;
		},
		isCurrent(seq) {
			return !stopped && seq === sequence;
		},
		isScheduled() {
			return timer !== null;
		},
	};
};

// ---- the detail of one run: the verdict ----

const PASS_MARK_LABELS = {
	zeroStoploss: '0 stop-losses',
	zeroBadTrades: '0 bad trades',
	goodTrades: 'at least 20 good trades',
	beatsBaseline: 'beats the baseline (compared at 2 decimals)',
	neighbourCheck: 'checked by the engine (RSI / Stochastic RSI in Enter)',
};

// The good-trade minimum every run of daemon 0.11.0 and older was judged by; a run stored without metadata.minGoodTrades
// keeps it (D-16)
const LEGACY_MIN_GOOD_TRADES = 20;

// UI-22: the daemon (v1.2 runs) stores the neighbour steps it judged with and the profit rule that applied. A run is v1.2
// exactly when both steps are whole numbers 0..=10; every other run (v1.0, v1.1, garbage) keeps today's marks and texts
const NEIGHBOUR_STEP_MAX = 10;

const isCount = value => isNumber(value) && Number.isInteger(value) && value >= 0;

const isStep = value => isCount(value) && value <= NEIGHBOUR_STEP_MAX;

const neighbourStepsOf = metadata => {
	const steps = metadata?.neighbourSteps;
	if (!steps || typeof steps !== 'object' || Array.isArray(steps)) {
		return null;
	}
	return isStep(steps.level) && isStep(steps.other) ? { level: steps.level, other: steps.other } : null;
};

// D-16: the daemon stores the good-trade minimum it judged the run with (metadata.minGoodTrades, daemon 0.12.0); the
// browser never derives one. Only a safe whole number of at least 1 counts, every other value (and a run stored without
// one) gives LEGACY_MIN_GOOD_TRADES
const minGoodTradesOf = metadata => {
	const minimum = metadata?.minGoodTrades;
	return isNumber(minimum) && Number.isSafeInteger(minimum) && minimum >= 1 ? minimum : LEGACY_MIN_GOOD_TRADES;
};

// D-37: the label reads correctly at 1 (singular) and at every other minimum (plural)
const goodTradesLabel = minimum => (minimum === 1 ? 'at least 1 good trade' : `at least ${minimum} good trades`);

// 'higher' | 'clean' as the daemon stored it (metadata.rules.profitRule); the browser never derives which rule applied
const profitRuleOf = metadata => {
	const rule = metadata?.rules?.profitRule;
	return rule === 'higher' || rule === 'clean' ? rule : null;
};

const countWord = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// What the suggestion removes from a flawed baseline: '3 stop-losses and 1 bad trade' (singular for exactly 1, a zero part
// left out); null when both counts are 0 or not whole numbers
const removalText = baselineStats => {
	const stoplosses = baselineStats?.stoplossCount;
	const badTrades = baselineStats?.badTrades;
	const parts = [];
	if (isCount(stoplosses) && stoplosses > 0) {
		parts.push(countWord(stoplosses, 'stop-loss', 'stop-losses'));
	}
	if (isCount(badTrades) && badTrades > 0) {
		parts.push(countWord(badTrades, 'bad trade', 'bad trades'));
	}
	return parts.length === 0 ? null : parts.join(' and ');
};

const stepGroupText = (name, steps) => (steps === 0 ? `${name} off` : `${name} ±${countWord(steps, 'step', 'steps')}`);

// 'threshold levels ±2 steps, other settings ±1 step' (a group at 0 reads 'off'); null when both are 0 or not valid steps
const stepsText = steps => {
	if (!isStep(steps?.level) || !isStep(steps?.other) || (steps.level === 0 && steps.other === 0)) {
		return null;
	}
	return `${stepGroupText('threshold levels', steps.level)}, ${stepGroupText('other settings', steps.other)}`;
};

// D-32 / D-33: the stop-loss cover a run was judged by and the Total profit it needed (metadata.minStoplossCover and
// metadata.minTotalProfit, daemon 0.13.0), plus the stop-loss it ran at (metadata.runStoploss) and, from daemon 0.13.1, the
// stop-loss the bar is a multiple of (metadata.coverStoploss, see coverBasisOf). Only a JSON number counts:
// a text, a missing key or an out-of-range value is null, and the browser never derives one from today's config
const stoplossCoverOf = metadata => {
	const cover = metadata?.minStoplossCover;
	return isNumber(cover) && cover >= STOPLOSS_COVER_RANGE[0] && cover <= STOPLOSS_COVER_RANGE[1] ? cover : null;
};

const minTotalProfitOf = metadata => {
	const bar = metadata?.minTotalProfit;
	return isNumber(bar) && bar >= 0 ? bar : null;
};

const runStoplossOf = metadata => (isNumber(metadata?.runStoploss) ? metadata.runStoploss : null);

// 1.5 -> '1.5', 1 -> '1', 1.25 -> '1.25' (no trailing zeros)
const coverText = cover => String(Number(cover));

const stoplossesWord = cover => (cover === 1 ? 'stop-loss' : 'stop-losses');

// D-37: a whole-number minimum of the header and the labels
const goodTradesWord = minimum => countWord(minimum, 'good trade', 'good trades');

const HEADER_COVER_SUFFIX = " (total profit over the run's window)";

// D-32 / D-04: the values a run was judged by, as the pieces of the detail header, in the order minimum, cover, neighbour
// steps. Each value comes from the stored metadata first and else, for a queued or running run, from the run row's own
// run... key (chosen at Start); a run with neither shows nothing for that value (no legacy 20, no config value)
const runJudgingHeaderText = run => {
	const metadata = run?.metadata;
	const pieces = [];
	const minimum = [metadata?.minGoodTrades, run?.runmingoodtrades].find(
		value => isCount(value) && value >= MIN_GOOD_TRADES_RANGE[0] && value <= MIN_GOOD_TRADES_RANGE[1],
	);
	if (minimum !== undefined) {
		pieces.push(`minimum ${goodTradesWord(minimum)}`);
	}
	const cover = stoplossCoverOf(metadata) ?? stoplossCoverOf({ minStoplossCover: run?.runstoplosscover });
	if (cover !== null) {
		pieces.push(cover === 0 ? 'cover off' : `cover ${coverText(cover)} ${stoplossesWord(cover)}${HEADER_COVER_SUFFIX}`);
	}
	let steps = neighbourStepsOf(metadata);
	if (steps === null && isStep(run?.runneighbourlevelsteps) && isStep(run?.runneighbourothersteps)) {
		steps = { level: run.runneighbourlevelsteps, other: run.runneighbourothersteps };
	}
	if (steps !== null) {
		const text = stepsText(steps);
		pieces.push(text === null ? 'neighbour check off' : `neighbour steps: ${text}`);
	}
	return pieces;
};

const MIN_CLEAN_CENTS = 1;
const MIN_CLEAN_TEXT = '0.01/day';
const HIGHER_TEXT = 'higher daily profit (compared at 2 decimals)';
// D-36 (WR-75): both neighbour steps are chosen per run, so the mark names the run, not the daemon config
const NEIGHBOUR_OFF_TEXT = 'off for this run';

// D-09 / D-10: the daily profit mark of a v1.2 run follows the rule the daemon stored; null when no rule is stored (the
// run then keeps today's mark)
const ruleProfitMark = ({ metadata, suggested, base, baselineStats }) => {
	const rule = profitRuleOf(metadata);
	if (rule === null) {
		return null;
	}
	const removal = removalText(baselineStats);
	if (rule === 'clean') {
		return {
			label:
				removal === null
					? `better: a clean config of at least ${MIN_CLEAN_TEXT}`
					: `better: removes the baseline's ${removal} (at least ${MIN_CLEAN_TEXT})`,
			pass: suggested !== null && suggested >= MIN_CLEAN_CENTS,
		};
	}
	const flawed = metadata.rules.baselineFlawed === true && removal !== null;
	return {
		label: flawed ? `higher daily profit; also removes the baseline's ${removal}` : HIGHER_TEXT,
		pass: suggested !== null && base !== null && suggested > base,
	};
};

// D-11: the Neighbour check row of a v1.2 run names the steps and the winner's counts; both steps 0 is a grey dash
const neighbourMark = (steps, metadata) => {
	const text = stepsText(steps);
	if (text === null) {
		return { label: NEIGHBOUR_OFF_TEXT, pass: null };
	}
	const tested = metadata?.rules?.neighbours?.tested;
	const skipped = metadata?.rules?.neighbours?.skipped;
	const counts =
		isCount(tested) && isCount(skipped)
			? ` (${countWord(tested, 'neighbour', 'neighbours')} tested, ${skipped} skipped)`
			: '';
	return { label: `passed: ${text}${counts}`, pass: true };
};

// D-45 / D-47: the stop-loss the bar is a multiple of (the stop-loss the run was judged at). daemon 0.13.1 stores it as
// metadata.coverStoploss (the strategy's own stop-loss when the run started); a run judged by daemon 0.13.0 has none and was
// judged at its run stop-loss (metadata.runStoploss). Only a JSON number of at least 0 counts as coverStoploss; null when
// neither is stored. This is the judged basis: the bar, the pass and the 'at' text use it, never the count (see coverCountBasisOf)
const coverBasisOf = metadata => {
	const own = metadata?.coverStoploss;
	if (isNumber(own) && own >= 0) {
		return { stoploss: own, own: true };
	}
	const run = runStoplossOf(metadata);
	return run === null ? null : { stoploss: run, own: false };
};

// The stop-loss the count of covered stop-losses is of (Marco's decision of 2026-10-21, refining D-44 / D-45 / D-47): always
// the strategy's own, so two runs can be compared. It takes the judged basis; when that is null or already the strategy's own
// (coverStoploss decides) it is returned unchanged, a run with no stored stop-loss gets no count basis. A run judged by daemon
// 0.13.0 counts on metadata.strategyStoploss, read as the header reads it, when it is above 0 and differs from the run's
// stop-loss in hundredths; otherwise the judged basis stays. The bar and the pass never use this basis
const coverCountBasisOf = metadata => {
	const judged = coverBasisOf(metadata);
	if (judged === null || judged.own) {
		return judged;
	}
	const strategy = strategyStoplossValue(metadata?.strategyStoploss);
	if (strategy !== null && strategy > 0 && Math.round(strategy * 100) !== Math.round(judged.stoploss * 100)) {
		return { stoploss: strategy, own: true };
	}
	return judged;
};

// How many stop-losses the Total profit covers: total profit / the basis stop-loss, the ratio the daemon's judge checks against
// the cover (bar = cover x basis). FLOORED, never rounded up, so the count can never contradict the pass mark (22.49 at a
// 22.50 bar reads 1.4, not 1.5). Integer hundredths and one decimal; a cover with two decimals counts to two decimals so a
// passing run never shows a count below its cover (18.75 at basis 15 and cover 1.25 reads 1.25, not 1.2). A loss is 0.
// Null (no count) when the total profit or the basis is missing, or the basis is not above 0
const coveredStoplossesText = (totalProfit, basisStoploss, cover) => {
	const totalCents = profitCents(totalProfit);
	if (totalCents === null || basisStoploss === null || !(basisStoploss > 0)) {
		return null;
	}
	const decimals = Math.round(cover * 100) % 10 === 0 ? 1 : 2;
	const unit = 10 ** decimals;
	const stoplossCents = Math.round(basisStoploss * 100);
	if (stoplossCents <= 0) {
		return null;
	}
	const floored = Math.max(0, Math.floor((totalCents * unit) / stoplossCents));
	return (floored / unit).toFixed(decimals);
};

// D-33: the Total profit row's mark. The bar is the Total profit the run stored (metadata.minTotalProfit); the pass compares
// integer hundredths so a total equal to the bar passes and one hundredth below fails. The text starts with the count of
// stop-losses the total profit covers (of the strategy's own stop-loss when stored), then the cover needed and the bar with
// the stop-loss it is a multiple of (D-45: the strategy's own, or for a run of daemon 0.13.0 the run's, the one it was judged
// at). A run of daemon 0.13.0 that counts on the strategy's stop-loss words the cover and its judged stop-loss instead of
// 'needed', so a count below the cover never reads as a miss next to a pass. A run stored without a cover has no mark (null);
// cover 0 is a grey dash reading 'cover off'; a cover without a stored bar says so and passes neither way; without a stored
// basis or a numeric total profit the text keeps the head 'covers at least <cover>' and shows no count
const coverMark = (stats, metadata) => {
	const cover = stoplossCoverOf(metadata);
	if (cover === null) {
		return null;
	}
	if (cover === 0) {
		return { label: 'cover off', pass: null };
	}
	const bar = minTotalProfitOf(metadata);
	if (bar === null) {
		return { label: `covers at least ${coverText(cover)} ${stoplossesWord(cover)}`, pass: null };
	}
	const basis = coverBasisOf(metadata);
	let at = '';
	if (basis !== null) {
		at = basis.own ? ` at the strategy's stop-loss ${basis.stoploss} %` : ` at stop-loss ${basis.stoploss} %`;
	}
	const total = profitCents(stats?.totalProfit);
	const pass = total !== null && total >= Math.round(bar * 100);
	const counted = coverCountBasisOf(metadata);
	const count = coveredStoplossesText(stats?.totalProfit, counted === null ? null : counted.stoploss, cover);
	if (count !== null && basis !== null && !basis.own && counted.own) {
		const coverNeeded = coverText(cover);
		const head = `covers ${count} of the strategy's ${counted.stoploss} % stop-losses; judged at ${coverNeeded} x stop-loss ${basis.stoploss} %`;
		return { label: `${head} (total profit at least ${bar.toFixed(2)})`, pass };
	}
	const head =
		count === null
			? `covers at least ${coverText(cover)} ${stoplossesWord(cover)}`
			: `covers ${count} ${count === '1' ? 'stop-loss' : 'stop-losses'}; at least ${coverText(cover)} needed`;
	return { label: `${head} (total profit at least ${bar.toFixed(2)}${at})`, pass };
};

// The five must-haves of a suggestion against the stored numbers. A run without stored neighbour steps (v1.0 / v1.1) gets
// the marks it always had: the neighbour check is not stored as a field there, a done run passed it by construction (a
// qualifier that fails it is never the winner). A v1.2 run reads its stored rule and steps (UI-22); a mark's text equals
// its label
const passMarks = ({ stats, baseline, metadata }) => {
	const suggested = profitCents(stats?.dailyProfit);
	const base = profitCents(baseline?.stats?.dailyProfit);
	const passes = {
		zeroStoploss: isNumber(stats?.stoplossCount) && stats.stoplossCount === 0,
		zeroBadTrades: isNumber(stats?.badTrades) && stats.badTrades === 0,
		goodTrades: isNumber(stats?.goodTrades) && stats.goodTrades >= minGoodTradesOf(metadata),
		beatsBaseline: suggested !== null && base !== null && suggested > base,
		neighbourCheck: true,
	};
	const overrides = { goodTrades: { label: goodTradesLabel(minGoodTradesOf(metadata)), pass: passes.goodTrades } };
	const steps = neighbourStepsOf(metadata);
	if (steps !== null) {
		const profit = ruleProfitMark({ metadata, suggested, base, baselineStats: baseline?.stats });
		if (profit !== null) {
			overrides.beatsBaseline = profit;
		}
		overrides.neighbourCheck = neighbourMark(steps, metadata);
	}
	const marks = Object.keys(PASS_MARK_LABELS).map(id => {
		const override = overrides[id];
		const label = override ? override.label : PASS_MARK_LABELS[id];
		return { id, label, pass: override ? override.pass : passes[id], text: label };
	});
	// D-33: a sixth mark, only for a run that stored a cover (PASS_MARK_LABELS stays the five ids of every run)
	const cover = coverMark(stats, metadata);
	if (cover !== null) {
		marks.push({ id: 'stoplossCover', label: cover.label, pass: cover.pass, text: cover.label });
	}
	return marks;
};

const DASH = '—';

const countText = value => (isNumber(value) ? String(value) : DASH);

// D-35: the Days row is a whole number (190.61458333333334 -> '191'), rounded half up
const daysText = value => (isNumber(value) ? String(Math.round(value)) : DASH);

const profitText = value => formatProfit(value) ?? DASH;

// The rows of the Baseline (re-run) | Suggested | Must-have table; mark is the id of the pass mark of a row, or null
const statsRows = ({ stats, baseline, metadata }) => {
	const base = baseline?.stats;
	// the Total profit row names the cover mark only when passMarks produces it (a MarkNode needs a mark to draw)
	const totalProfitMark = coverMark(stats, metadata) === null ? null : 'stoplossCover';
	const dailyText = value => {
		const text = formatProfit(value);
		return text === null ? DASH : `${text}/day`;
	};
	return [
		{
			key: 'dailyProfit',
			label: 'Daily profit',
			baseline: dailyText(base?.dailyProfit),
			suggested: dailyText(stats?.dailyProfit),
			mark: 'beatsBaseline',
		},
		{
			key: 'totalProfit',
			label: 'Total profit',
			baseline: profitText(base?.totalProfit),
			suggested: profitText(stats?.totalProfit),
			mark: totalProfitMark,
		},
		{
			key: 'totalDays',
			label: 'Days',
			baseline: daysText(base?.totalDays),
			suggested: daysText(stats?.totalDays),
			mark: null,
		},
		{
			key: 'goodTrades',
			label: 'Good trades',
			baseline: countText(base?.goodTrades),
			suggested: countText(stats?.goodTrades),
			mark: 'goodTrades',
		},
		{
			key: 'badTrades',
			label: 'Bad trades',
			baseline: countText(base?.badTrades),
			suggested: countText(stats?.badTrades),
			mark: 'zeroBadTrades',
		},
		{
			key: 'stoplossCount',
			label: 'Stop-losses',
			baseline: countText(base?.stoplossCount),
			suggested: countText(stats?.stoplossCount),
			mark: 'zeroStoploss',
		},
		{
			key: 'profitTrailingCount',
			label: 'Profit-trailing closes',
			baseline: countText(base?.profitTrailingCount),
			suggested: countText(stats?.profitTrailingCount),
			mark: null,
		},
	];
};

const STABILITY_V12_SUFFIX =
	'one-step neighbours of the changed settings also meet every requirement (informational, separate from the neighbour check)';

// null without a stability check (only a done run has one). A v1.2 run (metadata with valid neighbour steps) words it as
// the informational check it is, separate from the neighbour check
const stabilityText = (stability, metadata) => {
	if (
		!stability ||
		typeof stability !== 'object' ||
		!isNumber(stability.neighbours) ||
		!isNumber(stability.qualifying)
	) {
		return null;
	}
	const text =
		neighbourStepsOf(metadata) === null
			? `${stability.qualifying} of ${stability.neighbours} neighbours still qualify`
			: `${stability.qualifying} of ${stability.neighbours} ${STABILITY_V12_SUFFIX}`;
	if (stability.complete === false) {
		const tested = isNumber(stability.evaluated) ? stability.evaluated : '?';
		return `${text} (check incomplete: ${tested} of ${stability.neighbours} tested)`;
	}
	return text;
};

// UTC milliseconds as 'YYYY-MM-DD HH:mm' (the engine pins its candle window to 00:00 UTC)
const utcText = ms => {
	if (!isNumber(ms)) {
		return null;
	}
	const date = new Date(ms);
	if (Number.isNaN(date.getTime())) {
		return null;
	}
	return date.toISOString().slice(0, 16).replace('T', ' ');
};

// 'NVDA 1440 min: <first> – <last> UTC, 6000 candles, cutoff <cutoff> UTC'; null without a window
const windowText = window => {
	if (!window || typeof window !== 'object') {
		return null;
	}
	const head = [window.asset, isNumber(window.intervalMin) ? `${window.intervalMin} min` : null]
		.filter(Boolean)
		.join(' ');
	const parts = [];
	const first = utcText(window.firstMs);
	const last = utcText(window.lastMs);
	if (first && last) {
		parts.push(`${first} – ${last} UTC`);
	}
	if (isNumber(window.count)) {
		parts.push(`${window.count} candles`);
	}
	const cutoff = utcText(window.cutoffMs);
	if (cutoff) {
		parts.push(`cutoff ${cutoff} UTC`);
	}
	if (parts.length === 0) {
		return head || null;
	}
	return head ? `${head}: ${parts.join(', ')}` : parts.join(', ');
};

const baselineNote = run => {
	const text = windowText(run?.metadata?.window ?? run?.baseline?.window);
	const stoploss = runStoploss(run);
	const withStoploss = stoploss === null ? '' : ` with stop-loss ${formatPercent(stoploss)}`;
	return `Baseline = the strategy's settings${withStoploss} re-run on the search window${text ? ` (${text})` : ''}.`;
};

const EARLIER_RULE_NOTE =
	'This run was found under the earlier rule, which allowed bad trades. Check the bad trades above before you apply it.';

// A done run that still has bad trades was found before the 0 bad trades rule. Read from the run's own stats only (no
// engine version, no server field): the run stays appliable, the user decides after seeing the cross and this note
const earlierRuleNote = run => {
	if (run?.status === 'done' && isNumber(run.stats?.badTrades) && run.stats.badTrades > 0) {
		return EARLIER_RULE_NOTE;
	}
	return null;
};

const NO_STORED_RESULT =
	'The strategy has no stored daily result right now (it is recomputed after a Save or an Apply).';

// The stored daily result of the saved strategy comes from the daily window and may differ from the baseline
const storedResultNote = savedStrategy => {
	const daily = formatProfit(savedStrategy?.strategyresult?.result?.dailyProfit);
	if (daily === null) {
		return NO_STORED_RESULT;
	}
	return `The strategy's stored daily result is ${daily}/day. It comes from the daily window and may differ from the baseline above.`;
};

// Fixed text next to Apply (UI-06): the suggestion was searched and judged on one window of candles only
const HYPOTHESIS_TEXT =
	'This suggestion was found and judged on the same candle window it was searched on. Treat it as a hypothesis: it has not been tested on other candles.';

// Why a done run cannot be applied (the appliableReason of the server)
const APPLIABLE_REASON_TEXTS = {
	strategy_active: 'Disable for Trading first',
	already_applied: 'Applied on',
	strategy_missing: 'The strategy no longer exists',
};

// null when there is nothing to explain: an appliable run, or a run that is not done (it has no Apply at all). An
// outdated run is explained by outdatedWarning, also the strategy_changed reason of trading-api before 0.9.1
const appliableText = (run, formatDate) => {
	if (!run || run.status !== 'done' || run.appliable === true) {
		return null;
	}
	const reason = run.appliableReason;
	if (reason === 'strategy_changed') {
		return null;
	}
	if (reason === 'already_applied') {
		const when = run.appliedat && typeof formatDate === 'function' ? formatDate(run.appliedat) : null;
		return when ? `${APPLIABLE_REASON_TEXTS.already_applied} ${when}` : 'Already applied';
	}
	if (!reason) {
		return 'Apply is not available for this run';
	}
	return APPLIABLE_REASON_TEXTS[reason] ?? String(reason);
};

const OUTDATED_WARNING =
	'Strategy changed after this run started — outdated. Apply writes the suggested values over the current ones; the result above was judged on the older strategy.';

// The warning under Apply of a done run whose strategy changed after it started; null otherwise. Apply stays as the
// server says (Disable for Trading first still blocks it)
const outdatedWarning = run => (run?.status === 'done' && isOutdated(run) ? OUTDATED_WARNING : null);

const OUTDATED_CONFIRM =
	' This run is outdated: the strategy changed after it started, and the suggested values replace the current ones.';

const applyConfirmText = ({ changeCount, strategyName, outdated } = {}) => {
	const count = isNumber(changeCount) ? changeCount : 0;
	const name = typeof strategyName === 'string' && strategyName.trim() ? strategyName.trim() : 'this strategy';
	return `Apply ${count} changed ${count === 1 ? 'setting' : 'settings'} to ${name}?${
		outdated === true ? OUTDATED_CONFIRM : ''
	} The strategy result is cleared and recomputed.`;
};

// What the Apply dialog adds under its question (APPLY-06): a v1.1 run (a numeric runstoploss) offers an unchecked
// "also set stop-loss to X%" option, shown with the stop-loss the saved strategy has now when that is a number; a
// strategy that already has the run's stop-loss (strict equality, the rule trading-api skips the change by) gets a
// line instead of the option; a run without its own stop-loss (v1.0) gets nothing and applies exactly as before.
// kind: none | already (text) | checkbox (label)
const applyCheckboxView = ({ run, savedStrategy } = {}) => {
	const runStop = runStoploss(run);
	if (runStop === null) {
		return { kind: 'none' };
	}
	const own = strategyStoplossValue(savedStrategy?.stoplosspercent);
	if (own !== null && own === runStop) {
		return { kind: 'already', text: `The strategy's stop-loss is already ${formatPercent(runStop)}.` };
	}
	const ownText = strategyStoplossText(own);
	const label = `also set stop-loss to ${formatPercent(runStop)}`;
	return { kind: 'checkbox', label: ownText === null ? label : `${label} (strategy now: ${ownText})` };
};

// The request body of an Apply: the run id, plus applystoploss true only for a strict true from a ticked box; false is
// never sent (trading-api reads the flag as === true and writes the run's stored stop-loss itself, never a browser value)
const applyPayload = ({ id, applyStoploss } = {}) => (applyStoploss === true ? { id, applystoploss: true } : { id });

// The toast text of an answered apply; a repeated apply is a success that changed nothing
const applyResultText = outcome => {
	if (!outcome || outcome.ok !== true) {
		return String(outcome?.error || 'The suggestion was not applied');
	}
	if (outcome.data?.alreadyApplied === true) {
		return 'Already applied: nothing changed';
	}
	return 'Applied: the daily result is being recomputed';
};

const DAY_MS = 86400000;

// The start (00:00 UTC) of the UTC day a millisecond value falls on; the engine pins its candle windows to it
const utcDayStart = ms => Math.floor(ms / DAY_MS) * DAY_MS;

const LATER_WINDOW_NOTE =
	'The daily result was computed on a later daily window than this run, so the numbers can differ.';
const MAYBE_LATER_WINDOW_NOTE =
	'The numbers may differ if the recompute ran after 00:00 UTC, on a later daily window than this run.';
const CHANGED_AFTER_APPLY_NOTE =
	'The strategy was changed after this Apply; its daily result no longer belongs to this suggestion.';

// The line under an applied run: the strategy's daily result as the daemon recomputed it after the Apply, next to the
// suggested one. Nothing is computed here: both numbers are stored values compared in cents. The recomputed result
// lives on the saved strategy (strategyresult is null from the Apply until the daemon has stored a new one) and stores
// no window, so a note says when the daily window is certainly (applied on a later UTC day than the run's window) or
// possibly later than the run's. null for a run that was never applied or without a saved strategy.
// state: recomputing | matches | differs | unknown (no suggested number to compare with) | changed
// Two more fields for a run with its own stop-loss (v1.1), derived from the saved strategy against runstoploss, never
// from a stored flag: stoplossLine ('Stop-loss set to 15%' / 'Stop-loss kept at 3.3%' / 'Stop-loss kept off', null once
// the strategy was changed after the Apply, for a v1.0 run and when the saved stop-loss is not a number) and
// stoplossNote, only in the differs state while the strategy's stop-loss is not the run's: it replaces both window
// notes, also when the recompute certainly ran on a later window, because the stop-loss is the cause we can name.
// A v1.1 patch never holds '/stoplosspercent', so a hand edit of the stop-loss after the Apply is detected against
// metadata.strategyStoploss, the strategy's own value when the run started: a saved value equal to neither the run's
// nor that start value was edited after the Apply, and the run reads 'changed'. Without that metadata nothing changes.
const recomputeView = ({ run, savedStrategy } = {}) => {
	if (!run || !run.appliedat || !savedStrategy) {
		return null;
	}
	const changes = Array.isArray(run.patch?.changes) ? run.patch.changes : [];
	const stillApplied = changes.every(
		change => typeof change?.path !== 'string' || isEqual(getAtPointer(savedStrategy, change.path), change.to),
	);
	const runStop = runStoploss(run);
	const own = strategyStoplossValue(savedStrategy.stoplosspercent);
	const startedWith = strategyStoplossValue(run.metadata?.strategyStoploss);
	const editedAfterApply =
		runStop !== null && own !== null && own !== runStop && startedWith !== null && startedWith !== own;
	if (!stillApplied || editedAfterApply) {
		return {
			state: 'changed',
			text: CHANGED_AFTER_APPLY_NOTE,
			windowNote: null,
			stoplossLine: null,
			stoplossNote: null,
		};
	}
	const ownText = strategyStoplossText(own);
	const stoplossKnown = runStop !== null && ownText !== null;
	let stoplossLine = null;
	if (stoplossKnown) {
		if (own === runStop) {
			stoplossLine = `Stop-loss set to ${formatPercent(runStop)}`;
		} else {
			stoplossLine = own === 0 ? 'Stop-loss kept off' : `Stop-loss kept at ${ownText}`;
		}
	}
	const appliedMs = Date.parse(run.appliedat);
	const cutoffMs = run.metadata?.window?.cutoffMs;
	const laterWindow = Number.isFinite(appliedMs) && isNumber(cutoffMs) && utcDayStart(appliedMs) > cutoffMs;
	const recomputed = formatProfit(savedStrategy.strategyresult?.result?.dailyProfit);
	if (recomputed === null) {
		return {
			state: 'recomputing',
			text: 'Recomputed daily result: recomputing…',
			windowNote: laterWindow ? LATER_WINDOW_NOTE : null,
			stoplossLine,
			stoplossNote: null,
		};
	}
	const suggested = formatProfit(run.stats?.dailyProfit);
	if (suggested === null) {
		return {
			state: 'unknown',
			text: `Recomputed daily result: ${recomputed}/day`,
			windowNote: laterWindow ? LATER_WINDOW_NOTE : null,
			stoplossLine,
			stoplossNote: null,
		};
	}
	const differs = profitCents(recomputed) !== profitCents(suggested);
	let windowNote = null;
	let stoplossNote = null;
	if (differs && stoplossKnown && own !== runStop) {
		stoplossNote = `The strategy kept its own stop-loss (${ownText}) while this run was searched at ${formatPercent(
			runStop,
		)}, so the recomputed result can differ.`;
	} else if (laterWindow) {
		windowNote = LATER_WINDOW_NOTE;
	} else if (differs) {
		windowNote = MAYBE_LATER_WINDOW_NOTE;
	}
	return {
		state: differs ? 'differs' : 'matches',
		text: `Recomputed daily result: ${recomputed}/day (suggested ${suggested}/day)`,
		windowNote,
		stoplossLine,
		stoplossNote,
	};
};

const FUNNEL_LABELS = {
	evaluated: 'Candidates evaluated',
	zeroStoploss: '… with 0 stop-losses',
	zeroBadTrades: '… and 0 bad trades',
	goodTrades20: '… and at least 20 good trades',
	beatsBaseline: '… and beating the baseline',
	rsiCheckPassed: '… and passing the neighbour check',
	neighbourCheckPassed: '… and passing the neighbour check',
};

const FUNNEL_COMMON_KEYS = ['evaluated', 'zeroStoploss', 'zeroBadTrades', 'goodTrades20', 'beatsBaseline'];

// D-34: the cover stage sits right after the good-trades stage, and only a funnel that stored it shows it
const FUNNEL_COVER_KEY = 'coversStoplosses';
const FUNNEL_COVER_FALLBACK_LABEL = '… and covers the stop-loss cover';

// D-36 (WR-75): with both neighbour steps at 0 the last stage is not a stage the run went through: the row says so, no count
const FUNNEL_NEIGHBOUR_OFF_LABEL = '… neighbour check off for this run';

const funnelCoverLabel = metadata => {
	const cover = stoplossCoverOf(metadata);
	if (cover === null || cover === 0) {
		return FUNNEL_COVER_FALLBACK_LABEL;
	}
	return `… and covers at least ${coverText(cover)} ${stoplossesWord(cover)}`;
};

// A v1.2 run stored against a flawed baseline met the profit rule (higher, or clean at the floor), not only a higher profit
const FUNNEL_FLAWED_BEATS_LABEL =
	'… and meeting the profit rule (higher, or clean at 0.01/day against a flawed baseline)';

// D-16: the good-trades row names the run's own minimum (20 for a run stored without one, exactly FUNNEL_LABELS.goodTrades20)
const funnelLabel = (key, flawed, metadata) => {
	if (key === 'beatsBaseline' && flawed) {
		return FUNNEL_FLAWED_BEATS_LABEL;
	}
	if (key === 'goodTrades20') {
		return `… and ${goodTradesLabel(minGoodTradesOf(metadata))}`;
	}
	if (key === FUNNEL_COVER_KEY) {
		return funnelCoverLabel(metadata);
	}
	return FUNNEL_LABELS[key];
};

// The stages are cumulative: each count includes the stages before it. A funnel stored before the 0 bad trades rule has
// no zeroBadTrades stage: that row is left out then, so an older not_found run shows the five stages it always had
// The last stage is neighbourCheckPassed when the run stored it (v1.2), else the legacy rsiCheckPassed row
const funnelRows = (funnel, metadata) => {
	if (!funnel || typeof funnel !== 'object') {
		return [];
	}
	const flawed = neighbourStepsOf(metadata) !== null && metadata.rules?.baselineFlawed === true;
	const keys = [
		...FUNNEL_COMMON_KEYS,
		isNumber(funnel.neighbourCheckPassed) ? 'neighbourCheckPassed' : 'rsiCheckPassed',
	];
	keys.splice(keys.indexOf('goodTrades20') + 1, 0, FUNNEL_COVER_KEY);
	const steps = neighbourStepsOf(metadata);
	const neighbourOff = steps !== null && steps.level === 0 && steps.other === 0;
	return keys
		.filter(key => key !== 'zeroBadTrades' || isNumber(funnel.zeroBadTrades))
		.filter(key => key !== FUNNEL_COVER_KEY || isNumber(funnel[FUNNEL_COVER_KEY]))
		.map((key, i, kept) => {
			if (neighbourOff && i === kept.length - 1) {
				return { key: 'neighbourCheckPassed', label: FUNNEL_NEIGHBOUR_OFF_LABEL, count: null };
			}
			return {
				key,
				label: funnelLabel(key, flawed, metadata),
				count: isNumber(funnel[key]) ? funnel[key] : null,
			};
		});
};

// Why a run could not qualify at all (the daemon's own text); null when there is none
const limitationsText = funnel => {
	const list = Array.isArray(funnel?.limitations)
		? funnel.limitations.filter(item => typeof item === 'string' && item)
		: [];
	return list.length > 0 ? list.join('\n') : null;
};

// A failed run: the readable error, else the finish reason label, else the raw code
const failureText = run => {
	const error = String(run?.error ?? '').trim();
	return error || finishReasonText(run?.finishreason);
};

const textOrNull = value => (typeof value === 'string' && value !== '' ? value : null);

// The AI's (untrusted) text apart from the engine's numbers: a label that says where it comes from, the summary, and the
// reason of one setting -- paired through explanation.changes[i].path, else reasons[i] by the index in the patch
const explanationView = (explanation, patchChanges) => {
	if (!explanation || typeof explanation !== 'object') {
		return null;
	}
	let label;
	if (explanation.source === 'ai') {
		label = explanation.model ? `AI commentary (model ${explanation.model})` : 'AI commentary';
	} else if (explanation.source === 'generated') {
		label = 'Generated without AI';
	} else {
		label = `Commentary (source ${String(explanation.source ?? 'not stored')})`;
	}
	const pairs = Array.isArray(explanation.changes) ? explanation.changes : [];
	const reasons = Array.isArray(explanation.reasons) ? explanation.reasons : [];
	const patch = Array.isArray(patchChanges) ? patchChanges : [];
	return {
		label,
		summary: typeof explanation.summary === 'string' ? explanation.summary : '',
		reasonFor: path => {
			const pair = pairs.find(item => item?.path === path);
			if (pair) {
				return textOrNull(pair.reason);
			}
			const index = patch.findIndex(item => item?.path === path);
			return index === -1 ? null : textOrNull(reasons[index]);
		},
	};
};

// The run metadata the user needs, from named keys only (jsonb reorders keys: never iterate metadata)
const metadataRows = metadata => {
	if (!metadata || typeof metadata !== 'object') {
		return [];
	}
	let advisor = null;
	if (metadata.advisor) {
		advisor = metadata.model ? `${metadata.model} (${metadata.advisor})` : String(metadata.advisor);
	}
	const budget = metadata.budget;
	return [
		{ label: 'Window', value: windowText(metadata.window) },
		{ label: 'Model (advisor)', value: advisor },
		{ label: 'Engine version', value: metadata.engineVersion ? String(metadata.engineVersion) : null },
		{
			label: 'Threads',
			value: isNumber(metadata.threadsUsed)
				? `${metadata.threadsUsed} of ${isNumber(metadata.threadsConfigured) ? metadata.threadsConfigured : '?'}`
				: null,
		},
		{ label: 'Rounds', value: isNumber(budget?.maxRounds) ? String(budget.maxRounds) : null },
		{
			label: 'Time budget',
			value: isNumber(budget?.budgetSec) ? `${Math.round((budget.budgetSec / 60) * 10) / 10} min` : null,
		},
	].filter(row => row.value !== null);
};

// A later-round AI failure ends the run with the best result so far: a note, not an error
const advisorFailureNote = metadata => {
	const failure = metadata?.advisorFailure;
	if (!failure || typeof failure !== 'object') {
		return null;
	}
	const where = isNumber(failure.round) ? ` in round ${failure.round}` : '';
	return `The AI stopped answering${where}; the best result found until then was kept.`;
};

// ---- the round trace: level 1 (a row per round) and level 2 (the proposals of one round) ----

const REJECTION_CODES = [
	'unknown_setting',
	'wrong_type',
	'non_finite',
	'out_of_range',
	'bad_choice',
	'stoploss_off_grid',
	'stoploss_required',
	'duplicate_path',
	'too_many_changes',
	'empty',
	'too_many_proposals',
	'out_of_order',
	'setting_fixed',
];

const OUTCOME_LABELS = {
	evaluated: 'evaluated',
	duplicate: 'duplicate of a config already tried',
	panicked: 'the engine failed on it',
	not_evaluated: 'not evaluated (out of time)',
};
REJECTION_CODES.forEach(code => {
	OUTCOME_LABELS[code] = `rejected: ${code.replace(/_/g, ' ')}`;
});
OUTCOME_LABELS.setting_fixed = 'rejected: changes a fixed setting';

// Never blank: an outcome this tab has no label for is shown as the code the daemon stored
const outcomeLabel = code => {
	if (has(OUTCOME_LABELS, code)) {
		return OUTCOME_LABELS[code];
	}
	return code ? String(code) : 'outcome not stored';
};

// One light row per round. bestDailyProfit is the best of ANY candidate of that round (not the run's best qualifier);
// a round without it shows a dash, never 0
const traceRows = roundtrace => {
	const rounds = Array.isArray(roundtrace?.rounds) ? roundtrace.rounds : [];
	return rounds.map(round => {
		const best = formatProfit(round?.bestDailyProfit);
		return {
			round: round?.round,
			tested: isNumber(round?.tested) ? round.tested : null,
			qualifiers: isNumber(round?.qualifiers) ? round.qualifiers : null,
			skipped: round?.skipped === true,
			bestText: best === null ? DASH : `best candidate ${best}/day`,
			proposalCount: Array.isArray(round?.proposals) ? round.proposals.length : 0,
		};
	});
};

// The proposals of ONE round with the changes in the form's labels. The trace stores no 'from', only the new value; the
// reason is the AI's (untrusted, clipped) text and stays a plain string. The keys are for the list rendering
const proposalRows = (round, strategy) => {
	const proposals = Array.isArray(round?.proposals) ? round.proposals : [];
	return proposals.map((proposal, index) => {
		const best = formatProfit(proposal?.best);
		const reason = typeof proposal?.reason === 'string' && proposal.reason.trim() !== '' ? proposal.reason : null;
		const changes = Array.isArray(proposal?.changes) ? proposal.changes : [];
		return {
			key: `proposal-${index}`,
			outcomeText: outcomeLabel(proposal?.outcome),
			qualified: proposal?.qualified === true,
			bestText: best === null ? null : `${best}/day`,
			reason,
			changes: changes.map((change, changeIndex) => {
				const { label, raw } = changeLabel(change?.path, strategy);
				return { key: `change-${changeIndex}`, label, title: raw, to: formatValue(change?.to) };
			}),
		};
	});
};

module.exports = {
	POLL_INTERVAL_MS,
	PAGE_SIZE,
	MAX_LOADED_RUNS,
	RUN_STOPLOSS_CHOICES,
	DEFAULT_RUN_STOPLOSS,
	parseRunStoploss,
	RUN_ROUNDS_CHOICES,
	DEFAULT_RUN_ROUNDS,
	parseRunRounds,
	roundsToggleState,
	roundsOfToggleState,
	MIN_GOOD_TRADES_RANGE,
	STOPLOSS_COVER_RANGE,
	NEIGHBOUR_STEPS_RANGE,
	parseMinGoodTrades,
	parseStoplossCover,
	parseNeighbourSteps,
	START_SETTING_PARSERS,
	JUDGING_START_KEYS,
	JUDGING_WIRE_KEYS,
	startRequestValues,
	START_PROBLEM_TEXTS,
	COVER_NEEDS_OWN_STOPLOSS_TEXT,
	startSettingsProblems,
	NEIGHBOUR_OFF_START_TEXT,
	neighbourOffStartText,
	EMPTY_START_SETTING_TEXT,
	emptyStartSettingText,
	resolveStartValues,
	initialStartState,
	createStartMemory,
	START_DEFAULTS_TIMEOUT_MS,
	createStartDefaultsLoader,
	formatPercent,
	startToastText,
	V10_GRID_LABEL,
	runStoploss,
	runStoplossLabel,
	V10_LIST_LABEL,
	runStoplossListLabel,
	strategyStoplossValue,
	strategyStoplossText,
	startHint,
	runStoplossHeaderText,
	roundsOf,
	runRoundsListLabel,
	runRoundsHeaderText,
	ACTIVE_STATUSES,
	STATUS_LABELS,
	STATUS_PILL_CLASSES,
	PILL_BASE_CLASS,
	statusView,
	isActiveRun,
	hasActiveRun,
	PAUSED_HINT,
	HELD_TEXT,
	runAlert,
	hasPausedRun,
	continueText,
	createContinueFlow,
	continueListener,
	profitCents,
	formatProfit,
	formatClock,
	formatMaxMinutes,
	progressView,
	compareRuns,
	mergeFirstPage,
	createRunPoller,
	RESULT_KEYS,
	unsavedEdits,
	appendRunPage,
	hasMorePages,
	FINISH_REASON_LABELS,
	finishReasonText,
	runFinishText,
	outcomeText,
	rowBadges,
	BADGE_CLASSES,
	appliabilitySignature,
	cancelNeedsConfirm,
	CANCEL_RUNNING_CONFIRM,
	CANCEL_PAUSED_CONFIRM,
	cancelConfirmText,
	UNSAVED_EDITS_TOAST,
	PASS_MARK_LABELS,
	neighbourStepsOf,
	profitRuleOf,
	minGoodTradesOf,
	LEGACY_MIN_GOOD_TRADES,
	goodTradesLabel,
	NEIGHBOUR_OFF_TEXT,
	FUNNEL_NEIGHBOUR_OFF_LABEL,
	daysText,
	stoplossCoverOf,
	minTotalProfitOf,
	runStoplossOf,
	coverText,
	stoplossesWord,
	coverBasisOf,
	coverCountBasisOf,
	coverMark,
	runJudgingHeaderText,
	removalText,
	stepsText,
	passMarks,
	statsRows,
	stabilityText,
	utcText,
	windowText,
	baselineNote,
	EARLIER_RULE_NOTE,
	earlierRuleNote,
	storedResultNote,
	FUNNEL_LABELS,
	funnelRows,
	limitationsText,
	failureText,
	explanationView,
	metadataRows,
	advisorFailureNote,
	OUTCOME_LABELS,
	outcomeLabel,
	traceRows,
	proposalRows,
	HYPOTHESIS_TEXT,
	APPLIABLE_REASON_TEXTS,
	appliableText,
	isOutdated,
	OUTDATED_WARNING,
	outdatedWarning,
	applyConfirmText,
	applyResultText,
	applyCheckboxView,
	applyPayload,
	utcDayStart,
	recomputeView,
};
